import {
  type OrganizedSettings,
  PanelBankConfigState,
} from '../../../components/PanelBank/types';
import {type MediaBrowserPanelConfig} from '../../../components/PanelMediaBrowser/common';
import {type SemanticLegendPalette} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/utils/colorPalettes';
import {
  type LinePlotSettings,
  type OrganizationPrefix,
  type RunColorOptions,
  type WorkspaceLayoutSettings,
} from '../../../components/WorkspaceDrawer/Settings/types';
import {type ColorPaletteOption} from '../../../util/colors';
import {EMPTY_PANEL_BANK_CONFIG} from '../../../util/panelbankConfigs';
import {setInShallowClone} from '../../../util/utility';
import type * as CustomRunColorsTypes from '../customRunColors/types';
import {EMPTY_CUSTOM_RUN_COLORS} from '../customRunColors/util';
import {addObjsImmutable} from '../normalize';
import {clearAllPanels} from '../panelBankConfig/clearPanelsUtils';
import type * as PanelBankConfigTypes from '../panelBankConfig/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type ActionType, type ViewReducerState} from '../reducerSupport';
import * as Actions from './actions';
import {type Ref as WorkspaceSettingsRef} from './types';

export const updateLinePlotWorkspaceSettings = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  settings: Partial<LinePlotSettings>
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          linePlot: {
            ...prevWorkspaceSettings.linePlot,
            ...settings,
          },
        },
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(
    ref,
    prevWorkspaceSettings
  );
  return [newState, inverseAction];
};

export const updateAutoOrganizePrefix = (
  state: ViewReducerState,
  workspaceSettingsRef: WorkspaceSettingsRef,
  panelBankConfigRef: PanelBankConfigTypes.Ref,
  autoOrganizePrefix: OrganizationPrefix | undefined
): [ViewReducerState, ActionType] => {
  const prevSectionRefs =
    state.parts[panelBankConfigRef.type][panelBankConfigRef.id].sectionRefs;
  const prevAutoOrganizePrefix =
    state.parts[workspaceSettingsRef.type][workspaceSettingsRef.id]
      ?.autoOrganizePrefix;

  const {parts: newParts, refs: newSectionRefs} = addObjsImmutable(
    state.parts,
    'panel-bank-section-config',
    panelBankConfigRef.viewID,
    [...EMPTY_PANEL_BANK_CONFIG.sections]
  );

  const newState = {
    ...state,
    parts: {
      ...newParts,
      [panelBankConfigRef.type]: {
        ...newParts[panelBankConfigRef.type],
        [panelBankConfigRef.id]: {
          ...newParts[panelBankConfigRef.type][panelBankConfigRef.id],
          state: PanelBankConfigState.Init, // ensures correct section order in next round of panel gen
          sectionRefs: newSectionRefs, // update sections with new refs
        },
      },
      [workspaceSettingsRef.type]: {
        ...newParts[workspaceSettingsRef.type],
        [workspaceSettingsRef.id]: {
          ...newParts[workspaceSettingsRef.type][workspaceSettingsRef.id],
          autoOrganizePrefix, // update the workspace autoOrganizePrefix setting
        },
      },
    },
  };

  const inverseAction = Actions.updateAutoOrganizePrefixUndo(
    workspaceSettingsRef,
    panelBankConfigRef,
    prevSectionRefs,
    prevAutoOrganizePrefix
  );

  return [newState, inverseAction];
};

export const updateAutoOrganizePrefixUndo = (
  state: ViewReducerState,
  workspaceSettingsRef: WorkspaceSettingsRef,
  panelBankConfigRef: PanelBankConfigTypes.Ref,
  sectionRefs: PanelBankSectionConfigTypes.Ref[],
  autoOrganizePrefix: OrganizationPrefix | undefined
): [ViewReducerState, ActionType] => {
  const prevSectionRefs =
    state.parts[panelBankConfigRef.type][panelBankConfigRef.id].sectionRefs;
  const prevAutoOrganizePrefix =
    state.parts[workspaceSettingsRef.type][workspaceSettingsRef.id]
      ?.autoOrganizePrefix;

  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [panelBankConfigRef.type]: {
        ...state.parts[panelBankConfigRef.type],
        [panelBankConfigRef.id]: {
          ...state.parts[panelBankConfigRef.type][panelBankConfigRef.id],
          sectionRefs, // reverts
        },
      },
      [workspaceSettingsRef.type]: {
        ...state.parts[workspaceSettingsRef.type],
        [workspaceSettingsRef.id]: {
          ...state.parts[workspaceSettingsRef.type][workspaceSettingsRef.id],
          autoOrganizePrefix, // reverts
        },
      },
    },
  };

  const inverseAction = Actions.updateAutoOrganizePrefixUndo(
    workspaceSettingsRef,
    panelBankConfigRef,
    prevSectionRefs,
    prevAutoOrganizePrefix
  );

  return [newState, inverseAction];
};

export const updateShouldAutoGeneratePanels = (
  prevState: ViewReducerState,
  workspaceSettingsRef: WorkspaceSettingsRef,
  panelBankConfigRef: PanelBankConfigTypes.Ref,
  newValue: WorkspaceLayoutSettings['shouldAutoGeneratePanels']
): [ViewReducerState, ActionType] => {
  // update setting
  let newState = setInShallowClone(
    prevState,
    [
      'parts',
      workspaceSettingsRef.type,
      workspaceSettingsRef.id,
      'shouldAutoGeneratePanels',
    ],
    newValue
  );

  // clear panels if transitioning between modes
  if (newValue === 'pending') {
    newState = clearAllPanels(
      newState,
      panelBankConfigRef,
      workspaceSettingsRef
    )[0];
  }

  // set up undo action
  const prevValue =
    prevState.parts[workspaceSettingsRef.type][workspaceSettingsRef.id]
      .shouldAutoGeneratePanels;
  const inverseAction = Actions.updateShouldAutoGeneratePanels(
    workspaceSettingsRef,
    panelBankConfigRef,
    prevValue
  );

  return [newState, inverseAction];
};

export const updateColorPaletteOption = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  newColorPalette: ColorPaletteOption | undefined
): [ViewReducerState, ActionType] => {
  const prevColorPalette = state.parts[ref.type][ref.id].colorPaletteOption;
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          colorPaletteOption: newColorPalette,
        },
      },
    },
  };

  const inverseAction = Actions.updateColorPaletteOption(ref, prevColorPalette);
  return [newState, inverseAction];
};

export const updateSemanticLegendPalette = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  palette: SemanticLegendPalette | undefined
): [ViewReducerState, ActionType] => {
  const prevPalette = state.parts[ref.type][ref.id].semanticLegendPalette;
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          semanticLegendPalette: palette,
        },
      },
    },
  };

  const inverseAction = Actions.updateSemanticLegendPalette(ref, prevPalette);
  return [newState, inverseAction];
};

export const updateWorkspaceLayoutSettings = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  settings: Partial<WorkspaceLayoutSettings>
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...prevWorkspaceSettings,
          ...settings,
        },
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(
    ref,
    prevWorkspaceSettings
  );
  return [newState, inverseAction];
};

export const setWorkspaceSettings = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  settings: OrganizedSettings
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: Object.assign({}, settings),
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(
    ref,
    prevWorkspaceSettings
  );
  return [newState, inverseAction];
};

export const updateSemanticLegendRunColorOptions = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  semanticLegendRunColorOptions: RunColorOptions
): [ViewReducerState, ActionType] => {
  const prevRunColorOptions = state.parts[ref.type][ref.id];
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          semanticLegendRunColorOptions,
        },
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(ref, prevRunColorOptions);
  return [newState, inverseAction];
};

/**
 * Sets the run color option and drops every custom run color, so that key-based
 * colors aren't shadowed by colors left behind by "Randomize colors" or the
 * per-run color picker. Both writes land in one undo entry.
 */
export const updateSemanticLegendRunColorOptionsAndClearCustomRunColors = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  customRunColorsRef: CustomRunColorsTypes.Ref,
  semanticLegendRunColorOptions: RunColorOptions
): [ViewReducerState, ActionType] =>
  writeRunColorOptionsAndCustomRunColors(
    state,
    ref,
    customRunColorsRef,
    semanticLegendRunColorOptions,
    EMPTY_CUSTOM_RUN_COLORS
  );

export const updateSemanticLegendRunColorOptionsAndClearCustomRunColorsUndo = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  customRunColorsRef: CustomRunColorsTypes.Ref,
  semanticLegendRunColorOptions: RunColorOptions | undefined,
  customRunColors: CustomRunColorsTypes.CustomRunColors
): [ViewReducerState, ActionType] =>
  writeRunColorOptionsAndCustomRunColors(
    state,
    ref,
    customRunColorsRef,
    semanticLegendRunColorOptions,
    customRunColors
  );

/**
 * Writes the run color option and the whole custom run colors map, returning the
 * inverse. Only the one setting is touched, so undoing this can't clobber an
 * unrelated workspace setting written since.
 */
const writeRunColorOptionsAndCustomRunColors = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  customRunColorsRef: CustomRunColorsTypes.Ref,
  semanticLegendRunColorOptions: RunColorOptions | undefined,
  customRunColors: CustomRunColorsTypes.CustomRunColors
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const prevCustomRunColors =
    state.parts[customRunColorsRef.type][customRunColorsRef.id];

  // Undoing back to a workspace that never had the setting should leave it
  // unset, not set to undefined.
  const {
    semanticLegendRunColorOptions: _omitted,
    ...workspaceSettingsWithoutRunColorOptions
  } = prevWorkspaceSettings;
  const nextWorkspaceSettings =
    semanticLegendRunColorOptions === undefined
      ? workspaceSettingsWithoutRunColorOptions
      : {...prevWorkspaceSettings, semanticLegendRunColorOptions};

  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: nextWorkspaceSettings,
      },
      [customRunColorsRef.type]: {
        ...state.parts[customRunColorsRef.type],
        [customRunColorsRef.id]: customRunColors,
      },
    },
  };

  const inverseAction =
    Actions.updateSemanticLegendRunColorOptionsAndClearCustomRunColorsUndo(
      ref,
      customRunColorsRef,
      prevWorkspaceSettings.semanticLegendRunColorOptions,
      prevCustomRunColors
    );
  return [newState, inverseAction];
};

export const updateWorkspaceMediaSettings = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  settings: Partial<MediaBrowserPanelConfig>
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          media: {
            ...prevWorkspaceSettings.media,
            ...settings,
          },
        },
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(
    ref,
    prevWorkspaceSettings
  );
  return [newState, inverseAction];
};

export const unsetWorkspaceMediaSetting = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  key: keyof MediaBrowserPanelConfig
): [ViewReducerState, ActionType] => {
  return unsetWorkspaceMediaSettings(state, ref, [key]);
};

export const unsetWorkspaceMediaSettings = (
  state: ViewReducerState,
  ref: WorkspaceSettingsRef,
  keys: Array<keyof MediaBrowserPanelConfig>
): [ViewReducerState, ActionType] => {
  const prevWorkspaceSettings = state.parts[ref.type][ref.id];
  const prevWorkspaceMediaSettings = prevWorkspaceSettings.media;
  const newMediaSettings = {...prevWorkspaceMediaSettings};
  for (const key of keys) {
    delete newMediaSettings[key];
  }
  const newState = {
    ...state,
    parts: {
      ...state.parts,
      [ref.type]: {
        ...state.parts[ref.type],
        [ref.id]: {
          ...state.parts[ref.type][ref.id],
          media: newMediaSettings,
        },
      },
    },
  };

  const inverseAction = Actions.setWorkspaceSettings(
    ref,
    prevWorkspaceSettings
  );
  return [newState, inverseAction];
};
